import {
  makeStyles,
  InputAdornment,
  Button as MaterialButton,
  IconButton,
  Chip,
  DialogActions,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import cn from 'classnames';
import { parse as parseQuery } from 'query-string';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Role } from '../../api/user/Role';
import { useGoalsContext } from '../../contexts/goals-context';
import { RouterContext } from '../../contexts/router-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { isMobile } from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { Link, Text, TextField, Button, ModalV2 } from '../common';
import GoalStatusSelect, {
  SelectGoalStatus,
} from '../common/goal-status-select';
import List from '../common/list';
import SelectVenturePopover from '../common/select-venture-popover';
import GoalCard from '../list-cards/goal-card';

const useStyles = makeStyles(() => ({
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  searchInput: {
    width: '100%',
  },
  searchInputWrapper: {
    width: '100%',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  filterChip: {
    backgroundColor: COLORS.COLOR_BLUE_BASE,
    '& .MuiChip-label': {
      color: COLORS.COLOR_ADDITIONAL_WHITE,
    },
    '& .MuiChip-deleteIcon': {
      color: COLORS.COLOR_ADDITIONAL_WHITE,
    },
  },
  iconButton: {
    padding: 7,
    borderRadius: '4px',
  },
  filterIconButton: {
    padding: 6,
    color: COLORS.COLOR_BLUE_BASE,
    borderColor: COLORS.COLOR_BLUE_BASE,
    borderWidth: 1,
    borderStyle: 'solid',

    '&:disabled': {
      borderColor: 'rgba(0, 0, 0, 0.26)',
    },

    '&:hover': {
      color: COLORS.COLOR_BLUE_LIGHTENED_5,
      background: 'transparent',
      borderColor: COLORS.COLOR_BLUE_LIGHTENED_5,
    },
  },
  filtersModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  modalActions: {
    padding: '32px 0 0',
  },
  searchAndButtonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',

    '& > div:first-child': {
      flexGrow: 1,
    },
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
}));

interface GoalsFilters {
  search?: string;
  status: SelectGoalStatus;
  venture?: { id: string; ventureName: string };
}

function FilterIconButton({
  filters,
  onFiltersUpdate,
  ventures,
  isVenturesLoading,
}: {
  filters: GoalsFilters;
  onFiltersUpdate: (newFilters: GoalsFilters) => void;
  ventures: any[];
  isVenturesLoading: boolean;
}) {
  const classes = useStyles();
  const { hasRole } = useContext(UserContext);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localStatus, setLocalStatus] = useState<SelectGoalStatus>(
    filters.status || 'ALL',
  );
  const [localVenture, setLocalVenture] = useState<
    { id: string; ventureName: string } | undefined
  >(filters.venture);

  const handleOpen = useCallback(() => {
    setLocalStatus(filters.status || 'ALL');
    setLocalVenture(filters.venture);
    setFiltersOpen(true);
  }, [filters.status, filters.venture]);

  const handleClose = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    // Update both filters at once
    const newFilters = {
      ...filters,
      status: localStatus,
      venture: localVenture,
    };
    onFiltersUpdate(newFilters);
    handleClose();
  }, [localStatus, localVenture, onFiltersUpdate, handleClose, filters]);

  const handleClearFilters = useCallback(() => {
    setLocalStatus('ALL');
    setLocalVenture(undefined);
  }, []);

  const hasActiveFilters = localStatus !== 'ALL' || !!localVenture;

  return (
    <>
      <IconButton
        className={cn(classes.iconButton, classes.filterIconButton)}
        data-testid='button-filter-goals'
        onClick={handleOpen}>
        <FilterListIcon />
      </IconButton>
      <ModalV2
        width='480px'
        open={filtersOpen}
        onClose={handleClose}
        titleProps={{ title: 'Filters' }}>
        <div className={classes.filtersModalContent}>
          <GoalStatusSelect
            status={localStatus}
            onChange={(status: SelectGoalStatus) => setLocalStatus(status)}
            isDisabled={isVenturesLoading}
          />
          {(hasRole(Role.Admin) || hasRole(Role.Manager)) && (
            <SelectVenturePopover
              onChange={(val) => setLocalVenture(val || undefined)}
              value={localVenture}
              isActive={!!localVenture}
              small
            />
          )}
        </div>
        <DialogActions className={classes.modalActions}>
          <Button
            variant='outlined'
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            data-testid='button-clear-filters'>
            Clear filters
          </Button>
          <Button
            variant='outlined'
            onClick={handleClose}
            data-testid='button-cancel-filters'>
            Cancel
          </Button>
          <Button onClick={handleApply} data-testid='button-apply-filters'>
            Apply
          </Button>
        </DialogActions>
      </ModalV2>
    </>
  );
}

export default function GoalsList() {
  const classes = useStyles();
  const { hasAccessToAction } = useContext(UserContext);
  const { prevPath } = useContext(RouterContext);
  const {
    goals,
    ventures,
    isVenturesLoading,
    filters,
    handleFiltersUpdate,
    handleFilterUpdate,
    isLoading,
    isPageLoading,
    hasMorePages,
    handleNextPage,
    loadError,
    hasActiveFilters,
    handleGoalSliderChange,
  } = useGoalsContext();

  const debouncedTimer = useRef<number>();
  const [searchValue, setSearchValue] = useState(filters.search || '');

  const prevGoalId = useMemo(() => {
    if (prevPath?.includes?.('/goals/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const goalsIndex = splitPath.indexOf('goals');
      const goalId = splitPath[goalsIndex + 1];

      if (goalId && goalId.length === 36) {
        return `card-${goalId}`;
      }
    } else if (prevPath?.includes?.('goalId=')) {
      const search = prevPath.split('?')[1];
      const query = parseQuery(search);
      if (query.goalId) {
        return `card-${query.goalId}`;
      }
    }
  }, [prevPath]);

  const handleSearchUpdate = useCallback(
    (search?: string) => {
      handleFilterUpdate('search', search);
    },
    [handleFilterUpdate],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          handleSearchUpdate(e.target.value.trim());
        } else {
          handleSearchUpdate();
        }
      }, 800) as unknown as number;
    },
    [handleSearchUpdate],
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    handleSearchUpdate();
  }, [handleSearchUpdate]);

  useEffect(() => {
    // Sync search value with filters
    if (filters.search) {
      setSearchValue(filters.search);
    } else {
      setSearchValue('');
    }
  }, [filters]);

  const filterComponent = isMobile() ? (
    <div className={classes.filterContainer}>
      <div className={classes.searchAndButtonRow}>
        <TextField
          className={cn(classes.searchInput, CLASS_TRACKING.FILTER_ELEMENT)}
          value={searchValue}
          onChange={handleSearch}
          placeholder='Search'
          disabled={isLoading || isPageLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  onClick={handleClearSearch}
                  data-testid='button-clear-search'>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          wrapperClassName={classes.searchInputWrapper}
          small
        />
        <FilterIconButton
          filters={filters}
          onFiltersUpdate={handleFiltersUpdate}
          ventures={ventures}
          isVenturesLoading={isVenturesLoading}
        />
      </div>
      {hasActiveFilters && (
        <div className={classes.filtersRow}>
          {filters.status !== 'ALL' && (
            <Chip
              label={filters.status.replace(/_/g, ' ')}
              onDelete={() => handleFilterUpdate('status', 'ALL')}
              className={classes.filterChip}
            />
          )}
          {filters.venture && (
            <Chip
              label={filters.venture.ventureName}
              onDelete={() => handleFilterUpdate('venture', undefined)}
              className={classes.filterChip}
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <TextField
      className={cn(classes.searchInput, CLASS_TRACKING.FILTER_ELEMENT)}
      value={searchValue}
      onChange={handleSearch}
      placeholder='Search'
      disabled={isLoading || isPageLoading}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      wrapperClassName={classes.searchInputWrapper}
      small
    />
  );

  return (
    <List
      prevId={prevGoalId}
      loadError={loadError}
      isLoading={isLoading}
      isPageLoading={isPageLoading}
      hasMorePages={hasMorePages}
      onNextPage={!filters.search ? handleNextPage : undefined}
      filter={filterComponent}>
      {!goals.length ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='goal-table-no-items'>
            This panel contains a list of goals
            {filters.search ? ` with a keyword "${filters.search}"` : ''}
            .<br />
            {hasAccessToAction('goal.create') &&
              'We were not able to find information you requested, but feel free to add something new!'}
          </Text>
          {hasAccessToAction('goal.create') && (
            <MaterialButton
              component={Link}
              to={Pages.NEW_GOAL}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              Add Goal
            </MaterialButton>
          )}
        </div>
      ) : (
        <div
          className={classes.listContainer}
          key={filters.search || filters.status || filters.venture?.id}>
          {goals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              cardId={`card-${goal.id}`}
              onGoalUpdate={(updatedGoal) => {
                handleGoalSliderChange({
                  id: updatedGoal.id,
                  progress: updatedGoal.progress,
                  status: updatedGoal.status,
                });
              }}
            />
          ))}
        </div>
      )}
    </List>
  );
}
